import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, BookOpen, Eye, Trash, X, Edit } from 'lucide-react';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { formatDate } from '../utils/Config';
import { 
    deleteTrainingProgram, 
    fetchTrainingPrograms,
    createTrainingProgram,
    updateTrainingProgram,
    fetchTrainingProgramById 
} from '../redux/TrainingProgramsSlice';
import { Oval } from 'react-loader-spinner';

const TrainingProgramSearch = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const { loading } = useSelector((state) => state.trainingPrograms);

    const debouncedSearch = useCallback(
        debounce((term) => {
            dispatch(fetchTrainingPrograms({
                page: 1,
                limit: 15,
                search: term
            }));
        }, 500),
        [dispatch]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div className="relative">
            <div className="relative">
                <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'
                        }`}
                    size={20}
                />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search training programs..."
                    className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
            </div>
        </div>
    );
};

const AddProgramModal = ({ isOpen, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [programName, setProgramName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!programName.trim()) {
            toast.error('Program name is required');
            return;
        }

        try {
            setIsSubmitting(true);
            await dispatch(createTrainingProgram({ name: programName.trim() })).unwrap();
            toast.success('Training program created successfully');
            setProgramName('');
            onSuccess();
            onClose();
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to create training program';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setProgramName('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Add Training Program</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="programName" className="block text-sm font-medium text-gray-700 mb-2">
                            Program Name
                        </label>
                        <input
                            type="text"
                            id="programName"
                            value={programName}
                            onChange={(e) => setProgramName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter program name..."
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <div className="flex justify-center items-center">
                                    <Oval
                                        visible={true}
                                        height="20"
                                        width="20"
                                        color="#ffffff"
                                        ariaLabel="oval-loading"
                                    />
                                </div>
                            ) : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ViewProgramModal = ({ isOpen, onClose, programId }) => {
    const dispatch = useDispatch();
    const currentProgram = useSelector(state => state.trainingPrograms.currentProgram);
    const { loading } = useSelector(state => state.trainingPrograms);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (isOpen && programId) {
            dispatch(fetchTrainingProgramById(programId));
        }
    }, [dispatch, isOpen, programId]);

    useEffect(() => {
        if (currentProgram) {
            setEditedName(currentProgram.name || currentProgram.title || '');
        }
    }, [currentProgram]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedName(currentProgram.name || currentProgram.title || '');
    };

    const handleSave = async () => {
        if (!editedName.trim()) {
            toast.error('Program name is required');
            return;
        }

        try {
            setIsUpdating(true);
            await dispatch(updateTrainingProgram({
                id: programId,
                name: editedName.trim()
            })).unwrap();
            toast.success('Training program updated successfully');
            setIsEditing(false);
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to update training program';
            toast.error(errorMessage);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleClose = () => {
        setIsEditing(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Training Program Details</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
                
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Oval
                            visible={true}
                            height="40"
                            width="40"
                            color="#3B82F6"
                            ariaLabel="oval-loading"
                        />
                    </div>
                ) : currentProgram ? (
                    <div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Program Name
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={isUpdating}
                                />
                            ) : (
                                <div className="p-3 bg-gray-50 rounded-md">
                                    {currentProgram.name || currentProgram.title}
                                </div>
                            )}
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Created At
                            </label>
                            <div className="p-3 bg-gray-50 rounded-md">
                                {formatDate(currentProgram.created_at)}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? (
                                            <div className="flex justify-center items-center">
                                                <Oval
                                                    visible={true}
                                                    height="20"
                                                    width="20"
                                                    color="#ffffff"
                                                    ariaLabel="oval-loading"
                                                />
                                            </div>
                                        ) : 'Save'}
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleEdit}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    <Edit className="h-4 w-4" />
                                    Edit
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center text-gray-500">
                        Program not found
                    </div>
                )}
            </div>
        </div>
    );
};

const TrainingProgramTable = ({ programs }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [programToDelete, setProgramToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedProgramId, setSelectedProgramId] = useState(null);
    const dispatch = useDispatch();

    const handleView = (id) => {
        setSelectedProgramId(id);
        setShowViewModal(true);
    };

    const handleDelete = async (id) => {
        try {
            setIsDeleting(true);
            await dispatch(deleteTrainingProgram(id)).unwrap();
            toast.success('Training program deleted successfully');
            setShowDeleteModal(false);
            setProgramToDelete(null);
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to delete training program';
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const confirmDelete = (program) => {
        setProgramToDelete(program);
        setShowDeleteModal(true);
    };

    return (
        <>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Program
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date Added
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {programs?.map((program) => (
                            <tr key={program.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                            {program?.image ? (
                                                <img
                                                    src={program.image}
                                                    alt=""
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                    <BookOpen className="h-6 w-6 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {program?.name || program?.title}
                                            </div>
                                            <div className="text-sm text-gray-500 line-clamp-1">
                                                {program?.description}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">{formatDate(program?.created_at)}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex gap-2 items-center">
                                        <button
                                            onClick={() => handleView(program.id)}
                                            className="text-blue-600 hover:text-blue-900 hover:underline flex gap-1 items-center justify-center"
                                            title="View Details"
                                        >
                                            <Eye className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => confirmDelete(program)}
                                            className="text-red-600 hover:text-red-700"
                                            title="Delete Program"
                                        >
                                            <Trash className="h-5 w-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showDeleteModal && programToDelete && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                        <h2 className="text-xl font-bold mb-4">
                            Delete Training Program
                        </h2>
                        <p>Are you sure you want to delete the training program "{programToDelete.name || programToDelete.title}"?</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setProgramToDelete(null);
                                }}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                onClick={() => handleDelete(programToDelete.id)}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                {isDeleting ? (
                                    <div className="flex justify-center items-center">
                                        <Oval
                                            visible={true}
                                            height="20"
                                            width="20"
                                            color="#ffff"
                                            ariaLabel="oval-loading"
                                        />
                                    </div>
                                ) : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ViewProgramModal
                isOpen={showViewModal}
                onClose={() => setShowViewModal(false)}
                programId={selectedProgramId}
            />
        </>
    );
};

const TrainingPrograms = () => {
    const dispatch = useDispatch();
    const programs = useSelector(state => state.trainingPrograms.items);
    const { loading, error, meta } = useSelector((state) => state.trainingPrograms);
    const [showAddModal, setShowAddModal] = useState(false);

    const handleAddSuccess = () => {
        dispatch(fetchTrainingPrograms({
            page: 1,
            limit: 15
        }));
    };

    useEffect(() => {
        dispatch(fetchTrainingPrograms({
            page: 1,
            limit: 15
        }));
    }, [dispatch]);

    return (
        <div className="p-4">
            <div className="flex flex-col gap-2 sm:flex-row justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Training Programs</h1>
                <TrainingProgramSearch />
            </div>

            <div className='flex justify-end gap-2 items-center mb-4'>
                <button
                    onClick={() => setShowAddModal(true)}
                    className='w-fit self-end font-lufga text-white bg-[#1330C5] hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center'
                >
                    Add Program
                </button>
            </div>

            <TrainingProgramTable programs={programs} />

            <AddProgramModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleAddSuccess}
            />
        </div>
    );
};

export default TrainingPrograms;